import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { RecipeModal } from '../components/RecipeModal';
import { PriceHistoryModal } from '../components/PriceHistoryModal';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Loader2,
  UtensilsCrossed,
  FolderOpen,
  ChevronRight,
  MoreVertical,
  FlaskConical,
  History,
  DollarSign,
  ImageIcon,
  Upload,
  X,
} from 'lucide-react';

export default function Menu() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Dialogs
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showPriceHistoryModal, setShowPriceHistoryModal] = useState(false);
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  // Image upload states
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageItemId, setImageItemId] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', display_order: 0 });
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    base_price: 0,
    category_id: '',
    kitchen_station: 'grill',
    preparation_time: 10,
    is_available: true,
    variants: [],
    add_ons: [],
  });

  const fetchData = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const [catResp, itemsResp] = await Promise.all([
        api.get('/categories', { params: { outlet_id: currentOutlet.id } }),
        api.get('/menu-items', { params: { outlet_id: currentOutlet.id } }),
      ]);
      setCategories(catResp.data);
      setMenuItems(itemsResp.data);
    } catch (error) {
      toast.error(t('messages.error.failed_fetch_menu'));
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        display_order: category.display_order || 0,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', display_order: categories.length });
    }
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, categoryForm);
        toast.success(t('messages.success.category_updated'));
      } else {
        await api.post('/categories', {
          ...categoryForm,
          outlet_id: currentOutlet.id,
          is_active: true,
        });
        toast.success(t('messages.success.category_created'));
      }
      setShowCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.failed_save_category'));
    }
  };

  const handleDeleteCategory = async (category) => {
    const itemCount = menuItems.filter(i => i.category_id === category.id).length;
    if (itemCount > 0) {
      toast.error(`Kategori tidak dapat dihapus karena masih memiliki ${itemCount} item.`);
      return;
    }
    setCategoryToDelete(category);
    setShowDeleteCategoryDialog(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await api.delete(`/categories/${categoryToDelete.id}`);
      toast.success('Kategori berhasil dihapus');
      setShowDeleteCategoryDialog(false);
      setCategoryToDelete(null);
      if (selectedCategory?.id === categoryToDelete.id) {
        setSelectedCategory(null);
      }
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.error === 'CATEGORY_NOT_EMPTY') {
        toast.error(errorDetail.message);
      } else {
        toast.error(errorDetail || 'Gagal menghapus kategori');
      }
      setShowDeleteCategoryDialog(false);
      setCategoryToDelete(null);
    }
  };

  // Menu Item handlers
  const openItemDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        name: item.name,
        description: item.description || '',
        base_price: item.base_price,
        category_id: item.category_id,
        kitchen_station: item.kitchen_station || 'grill',
        preparation_time: item.preparation_time || 10,
        is_available: item.is_available,
        variants: item.variants || [],
        add_ons: item.add_ons || [],
      });
    } else {
      setEditingItem(null);
      setItemForm({
        name: '',
        description: '',
        base_price: 0,
        category_id: selectedCategory?.id || categories[0]?.id || '',
        kitchen_station: 'grill',
        preparation_time: 10,
        is_available: true,
        variants: [],
        add_ons: [],
      });
    }
    setShowItemDialog(true);
  };

  const handleSaveItem = async () => {
    try {
      const payload = {
        ...itemForm,
        outlet_id: currentOutlet.id,
      };

      if (editingItem) {
        await api.put(`/menu-items/${editingItem.id}`, payload);
        toast.success(t('messages.success.item_updated'));
      } else {
        await api.post('/menu-items', payload);
        toast.success(t('messages.success.item_created'));
      }
      setShowItemDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.failed_save_item'));
    }
  };

  const handleDeleteItem = async (item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.delete(`/menu-items/${item.id}`);
      toast.success(t('messages.success.item_deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('messages.error.failed_to_delete'));
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      await api.put(`/menu-items/${item.id}`, { is_available: !item.is_available });
      fetchData();
      toast.success(`${item.name} is now ${!item.is_available ? 'available' : 'unavailable'}`);
    } catch (error) {
      toast.error(t('messages.error.failed_to_update'));
    }
  };

  // Image upload handlers
  const handleImageUpload = async (itemId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('messages.error.invalid_file_type'));
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('messages.error.file_too_large'));
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/menu-items/${itemId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(t('messages.success.image_uploaded'));
      fetchData();
      setShowImageDialog(false);
    } catch (error) {
      const msg = error.response?.data?.detail || t('messages.error.failed_upload_image');
      toast.error(msg);
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleImageDelete = async (itemId) => {
    if (!confirm(t('messages.confirm.remove_image'))) return;
    
    try {
      await api.delete(`/menu-items/${itemId}/image`);
      toast.success(t('messages.success.image_removed'));
      fetchData();
    } catch (error) {
      toast.error(t('messages.error.failed_remove_image'));
    }
  };
  
  const openImageDialog = (itemId) => {
    setImageItemId(itemId);
    setShowImageDialog(true);
  };

  // Variant handlers
  const addVariant = () => {
    setItemForm(prev => ({
      ...prev,
      variants: [...prev.variants, { id: crypto.randomUUID(), name: '', price_adjustment: 0 }],
    }));
  };

  const updateVariant = (index, field, value) => {
    setItemForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: field === 'price_adjustment' ? parseFloat(value) || 0 : value } : v
      ),
    }));
  };

  const removeVariant = (index) => {
    setItemForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  // Add-on handlers
  const addAddOn = () => {
    setItemForm(prev => ({
      ...prev,
      add_ons: [...prev.add_ons, { id: crypto.randomUUID(), name: '', price: 0 }],
    }));
  };

  const updateAddOn = (index, field, value) => {
    setItemForm(prev => ({
      ...prev,
      add_ons: prev.add_ons.map((a, i) =>
        i === index ? { ...a, [field]: field === 'price' ? parseFloat(value) || 0 : value } : a
      ),
    }));
  };

  const removeAddOn = (index) => {
    setItemForm(prev => ({
      ...prev,
      add_ons: prev.add_ons.filter((_, i) => i !== index),
    }));
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = !selectedCategory || item.category_id === selectedCategory.id;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const canManage = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.role);

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">Please select an outlet first</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="menu-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('menu.title')}</h1>
          <p className="text-muted-foreground">{t('menu.subtitle')}</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openCategoryDialog()} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('menu.add_category')}
            </Button>
            <Button onClick={() => openItemDialog()} data-testid="add-item-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('menu.add_item')}
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Categories Sidebar */}
        <Card className="w-64 shrink-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('menu.categories')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-300px)]">
              <div className="space-y-1 p-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full flex items-center justify-between p-2 rounded-md text-sm ${
                    !selectedCategory ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                  }`}
                  data-testid="category-all"
                >
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    {t('menu.all_items')}
                  </span>
                  <Badge variant="secondary">{menuItems.length}</Badge>
                </button>
                {categories.filter(c => c.is_active).map((category) => {
                  const itemCount = menuItems.filter(i => i.category_id === category.id).length;
                  const canDelete = itemCount === 0;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category)}
                      className={`w-full flex items-center justify-between p-2 rounded-md text-sm ${
                        selectedCategory?.id === category.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      <span className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4" />
                        {category.name}
                      </span>
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary">{itemCount}</Badge>
                        {canManage && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCategoryDialog(category);
                              }}
                              title={t('menu.edit_category')}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCategory(category);
                                }}
                                title={t('menu.delete_category')}
                                data-testid={`delete-category-${category.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Menu Items */}
        <div className="flex-1 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('menu.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="menu-search"
            />
          </div>

          {/* Items Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-64">
                <UtensilsCrossed className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t('empty_states.no_menu_items')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <Card key={item.id} className={!item.is_available ? 'opacity-60' : ''} data-testid={`item-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {/* Image Section */}
                      <div className="relative flex-shrink-0">
                        {item.image_url && item.image_url.length > 0 ? (
                          <div className="relative group">
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                              alt={item.name}
                              className="w-20 h-20 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="w-20 h-20 bg-muted rounded-lg items-center justify-center hidden">
                              <UtensilsCrossed className="h-8 w-8 text-muted-foreground" />
                            </div>
                            {canManage && (
                              <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-white hover:bg-white/20"
                                  onClick={() => openImageDialog(item.id)}
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-white hover:bg-white/20"
                                  onClick={() => handleImageDelete(item.id)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div 
                            className={`w-20 h-20 bg-muted rounded-lg flex items-center justify-center ${canManage ? 'cursor-pointer hover:bg-muted/80' : ''}`}
                            onClick={() => canManage && openImageDialog(item.id)}
                          >
                            {canManage ? (
                              <div className="flex flex-col items-center text-muted-foreground">
                                <ImageIcon className="h-6 w-6" />
                                <span className="text-xs mt-1">Add</span>
                              </div>
                            ) : (
                              <UtensilsCrossed className="h-8 w-8 text-muted-foreground" />
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{item.name}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-1">{item.description}</p>
                            <p className="font-mono text-lg font-semibold text-primary mt-1">
                              {formatCurrency(item.base_price)}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.variants?.length > 0 && (
                                <Badge variant="outline" className="text-xs">{item.variants.length} variants</Badge>
                              )}
                              {item.add_ons?.length > 0 && (
                                <Badge variant="outline" className="text-xs">{item.add_ons.length} add-ons</Badge>
                              )}
                            </div>
                          </div>
                          {canManage && (
                            <div className="flex flex-col items-end gap-1 ml-2">
                              <Switch
                                checked={item.is_available}
                                onCheckedChange={() => handleToggleAvailability(item)}
                                data-testid={`toggle-${item.id}`}
                              />
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openItemDialog(item)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit Item
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedItemForModal(item);
                                    setShowRecipeModal(true);
                                  }}>
                                    <FlaskConical className="h-4 w-4 mr-2" />
                                    Manage Recipe
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedItemForModal(item);
                                    setShowPriceHistoryModal(true);
                                  }}>
                                    <History className="h-4 w-4 mr-2" />
                                    Price History
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openImageDialog(item.id)}>
                                    <ImageIcon className="h-4 w-4 mr-2" />
                                    {item.image_url && item.image_url.length > 0 ? 'Change Image' : 'Add Image'}
                                  </DropdownMenuItem>
                                  {item.image_url && item.image_url.length > 0 && (
                                    <DropdownMenuItem 
                                      onClick={() => handleImageDelete(item.id)}
                                      className="text-destructive"
                                    >
                                      <X className="h-4 w-4 mr-2" />
                                      Remove Image
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? t('menu.edit_category') : t('menu.add_category')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('menu.category_name_label')}</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder={t('menu.category_name_placeholder')}
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label>{t('menu.description')}</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder={t('menu.description_placeholder')}
              />
            </div>
            <div>
              <Label>{t('menu.display_order')}</Label>
              <Input
                type="number"
                value={categoryForm.display_order}
                onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveCategory} data-testid="save-category-btn">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? t('menu.edit_item') : t('menu.add_item')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('menu.item_name_label')}</Label>
                <Input
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder={t('menu.item_name_placeholder')}
                  data-testid="item-name-input"
                />
              </div>
              <div>
                <Label>{t('menu.base_price')}</Label>
                <Input
                  type="number"
                  value={itemForm.base_price}
                  onChange={(e) => setItemForm({ ...itemForm, base_price: parseFloat(e.target.value) || 0 })}
                  data-testid="item-price-input"
                />
              </div>
            </div>

            <div>
              <Label>{t('menu.description')}</Label>
              <Textarea
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder={t('menu.item_description_placeholder')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('menu.category_label')}</Label>
                <Select
                  value={itemForm.category_id}
                  onValueChange={(val) => setItemForm({ ...itemForm, category_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('menu.select_category')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.is_active).map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('menu.kitchen_station')}</Label>
                <Select
                  value={itemForm.kitchen_station}
                  onValueChange={(val) => setItemForm({ ...itemForm, kitchen_station: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grill">{t('menu.kitchen_stations.grill')}</SelectItem>
                    <SelectItem value="drinks">{t('menu.kitchen_stations.drinks')}</SelectItem>
                    <SelectItem value="soup">{t('menu.kitchen_stations.soup')}</SelectItem>
                    <SelectItem value="prep">{t('menu.kitchen_stations.prep')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Variants */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t('menu.variants')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addVariant}>
                  <Plus className="h-3 w-3 mr-1" />
                  {t('menu.add_variant')}
                </Button>
              </div>
              {itemForm.variants.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('menu.no_variants')}</p>
              ) : (
                <div className="space-y-2">
                  {itemForm.variants.map((variant, index) => (
                    <div key={variant.id} className="flex gap-2 items-center">
                      <Input
                        placeholder={t('menu.variant_name')}
                        value={variant.name}
                        onChange={(e) => updateVariant(index, 'name', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder={t('menu.price_adjustment')}
                        value={variant.price_adjustment}
                        onChange={(e) => updateVariant(index, 'price_adjustment', e.target.value)}
                        className="w-32"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeVariant(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add-ons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t('menu.add_ons')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAddOn}>
                  <Plus className="h-3 w-3 mr-1" />
                  {t('menu.add_add_on')}
                </Button>
              </div>
              {itemForm.add_ons.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('menu.no_add_ons')}</p>
              ) : (
                <div className="space-y-2">
                  {itemForm.add_ons.map((addOn, index) => (
                    <div key={addOn.id} className="flex gap-2 items-center">
                      <Input
                        placeholder={t('menu.add_on_name')}
                        value={addOn.name}
                        onChange={(e) => updateAddOn(index, 'name', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder={t('common.price')}
                        value={addOn.price}
                        onChange={(e) => updateAddOn(index, 'price', e.target.value)}
                        className="w-32"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeAddOn(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveItem} data-testid="save-item-btn">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipe Modal */}
      <RecipeModal
        open={showRecipeModal}
        onOpenChange={setShowRecipeModal}
        menuItem={selectedItemForModal}
      />

      {/* Image Upload Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('menu.upload_image')}</DialogTitle>
            <DialogDescription>
              {t('menu.upload_image_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && imageItemId) {
                    handleImageUpload(imageItemId, file);
                  }
                }}
                className="hidden"
                id="image-upload-input"
                disabled={uploadingImage}
              />
              <label htmlFor="image-upload-input" className="cursor-pointer flex flex-col items-center">
                {uploadingImage ? (
                  <>
                    <Loader2 className="h-10 w-10 text-muted-foreground animate-spin mb-2" />
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                    <span className="text-sm font-medium">Click to upload</span>
                    <span className="text-xs text-muted-foreground">JPG, PNG, WebP (max 2MB)</span>
                  </>
                )}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImageDialog(false)} disabled={uploadingImage}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Modal */}
      <PriceHistoryModal
        open={showPriceHistoryModal}
        onOpenChange={setShowPriceHistoryModal}
        menuItem={selectedItemForModal}
        onPriceUpdated={fetchData}
      />

      {/* Delete Category Confirmation */}
      <AlertDialog open={showDeleteCategoryDialog} onOpenChange={setShowDeleteCategoryDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus kategori "{categoryToDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Kategori ini tidak memiliki item. Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCategoryToDelete(null)}>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-category-btn"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
